import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, Trash2, Save, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import EmailSettingsCard from "./EmailSettingsCard";
import ShippingSettingsCard from "./ShippingSettingsCard";
import Toggle from "@/components/app/Toggle";
import { Field, useValidatedForm, isBlank } from "@/components/app/FormKit";
import { btnOutline, btnPrimary, cardCls, fmtDateTime, pageSub, pageTitle } from "@/components/app/ui";

const KEY_RE = /^rzp_(test|live)_[A-Za-z0-9]{8,40}$/;
const SOURCE_TEXT = {
  database: "Using the keys saved here.",
  environment: "Using the keys from the server environment (RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET).",
  demo: "No keys are active: subscription payments auto-complete in DEMO mode and no money is collected.",
};

export default function AdminSettings() {
  const [cfg, setCfg] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [verify, setVerify] = useState(true);
  const [saving, setSaving] = useState(false);

  const validate = useCallback((f) => {
    const e = {};
    if (isBlank(f.key_id)) e.key_id = "Key ID is required.";
    else if (!KEY_RE.test(f.key_id.trim())) e.key_id = "Looks like rzp_test_XXXXXXXXXXXX or rzp_live_XXXXXXXXXXXX.";
    if (!isBlank(f.key_secret) && f.key_secret.trim().length < 12) e.key_secret = "That secret looks too short.";
    return e;
  }, []);
  const form = useValidatedForm({ key_id: "", key_secret: "" }, validate);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/settings/razorpay");
    setCfg(data); setEnabled(data.key_id ? data.enabled : true);
    form.reset({ key_id: data.key_id || "", key_secret: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load payment settings.")); }, [load]);

  const save = async () => {
    if (!form.check()) return;
    const id = form.values.key_id.trim();
    const needsSecret = !cfg.secret_set || id !== cfg.key_id;
    if (needsSecret && isBlank(form.values.key_secret)) { toast.error("Enter the Key Secret."); return; }
    if (id.startsWith("rzp_live_") && enabled && !window.confirm("These are LIVE keys. Vendors' subscription payments will charge real money and settle to this Razorpay account. Continue?")) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings/razorpay", { key_id: id, key_secret: form.values.key_secret.trim() || null, enabled, verify });
      setCfg(data); form.reset({ key_id: data.key_id, key_secret: "" });
      toast.success(data.enabled ? "Razorpay keys saved. Subscription payments will use them." : "Razorpay keys saved but switched off.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the saved Razorpay keys? Subscription payments fall back to the server environment keys, or to demo mode if there are none.")) return;
    try { const { data } = await api.delete("/admin/settings/razorpay"); setCfg(data); setEnabled(true); form.reset({ key_id: "", key_secret: "" }); toast.success("Saved keys removed."); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  if (!cfg) return <div className="text-slate-500 text-[13px]">Loading…</div>;
  const live = cfg.active_mode === "live";
  const demo = cfg.active_source === "demo";

  return (
    <div className="space-y-4 max-w-3xl" data-testid="admin-settings">
      <div>
        <h1 className={pageTitle}>Setup</h1>
        <p className={pageSub}>Payment gateway keys and the email service key for the whole platform.</p>
      </div>

      <div className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${demo ? "bg-orange-50 border-orange-200" : live ? "bg-green-50 border-green-200" : "bg-blue-50 border-blue-200"}`} data-testid="razorpay-status">
        <span className={`mt-0.5 ${demo ? "text-orange-600" : live ? "text-green-600" : "text-blue-600"}`}>{demo ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}</span>
        <div className="text-[13px]">
          <div className="font-bold text-neutral-900">{demo ? "Demo mode" : live ? "Live payments" : "Test mode"}</div>
          <div className="text-slate-600">{SOURCE_TEXT[cfg.active_source]}{!demo && !live && " Test payments do not move real money."}</div>
        </div>
      </div>

      <section className={`${cardCls} p-5`}>
        <div className="flex items-center gap-2 mb-1"><CreditCard size={16} className="text-blue-600" /><h2 className="text-[14px] font-bold">Razorpay</h2>
          {cfg.key_id && <span className="ml-auto flex items-center gap-2 text-[12px] text-slate-600">{enabled ? "Enabled" : "Disabled"}<Toggle checked={enabled} onChange={setEnabled} label="Use these keys" t="razorpay-enabled" /></span>}</div>
        <p className="text-[12px] text-slate-500 mb-4">Get the keys from the Razorpay Dashboard → Account &amp; Settings → API Keys. Use <b>test</b> keys to try the flow, then <b>live</b> keys to collect real payments.
          {" "}<a href="https://dashboard.razorpay.com/app/keys" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">Open dashboard <ExternalLink size={11} /></a></p>
        <div className="grid grid-cols-6 gap-x-3 gap-y-3">
          <Field className="col-span-6 sm:col-span-3" label="Key ID" required t="razorpay-key-id" placeholder="rzp_test_XXXXXXXXXXXX" autoComplete="off" {...form.bind("key_id")} />
          <Field className="col-span-6 sm:col-span-3" label="Key Secret" type="password" t="razorpay-key-secret" autoComplete="new-password"
            placeholder={cfg.secret_set ? `${cfg.secret_hint} (saved — leave blank to keep)` : "Paste your Key Secret"}
            hint="Stored encrypted. It is never shown again after saving." {...form.bind("key_secret")} />
        </div>
        <label className="mt-3 flex items-center gap-2 text-[12px] text-slate-600">
          <input type="checkbox" checked={verify} onChange={(e) => setVerify(e.target.checked)} data-testid="razorpay-verify" className="rounded border-slate-300" />
          Check the keys with Razorpay before saving
        </label>
        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
          <div className="text-[11px] text-slate-500">{cfg.updated_at ? `Last updated ${fmtDateTime(cfg.updated_at)}` : "No keys saved yet."}</div>
          <div className="flex gap-2">
            {cfg.key_id && <button onClick={remove} data-testid="razorpay-remove" className={btnOutline}><Trash2 size={14} /> Remove keys</button>}
            <button onClick={save} disabled={saving} data-testid="razorpay-save" className={btnPrimary}><Save size={14} /> {saving ? "Saving…" : "Save keys"}</button>
          </div>
        </div>
      </section>

      <ShippingSettingsCard />

      <EmailSettingsCard />

      <section className={`${cardCls} p-5 text-[12px] text-slate-600 space-y-1.5`}>
        <div className="flex items-center gap-2 text-[13px] font-bold text-neutral-900"><ShieldCheck size={15} className="text-green-600" /> How it works</div>
        <p>Vendors pay plan upgrades and monthly invoices through Razorpay Checkout; each payment is verified on the server with its signature before a plan is activated or an invoice marked paid.</p>
        <p>These keys are used only for subscription payments to the platform. Customers&apos; payments on vendor storefronts are separate and never use this account.</p>
      </section>
    </div>
  );
}
